import {
  createUuid,
  ReflectionCreateSchema,
  ReflectionSchema,
  ReflectionUpdateSchema,
  type CreateReflectionInput,
  type DuaReflection,
  type UpdateReflectionInput,
} from "../../domain";
import type { DuaWorkspaceDatabase } from "../database";
import {
  assertLiveReferences,
  EntityNotFoundError,
  parseEntityId,
  type RepositoryOptions,
} from "./repository-utils";

export class ReflectionRepository {
  readonly #database: DuaWorkspaceDatabase;
  readonly #now: () => string;
  readonly #createId: () => string;

  constructor(database: DuaWorkspaceDatabase, options: RepositoryOptions = {}) {
    this.#database = database;
    this.#now = options.now ?? (() => new Date().toISOString());
    this.#createId = options.createId ?? createUuid;
  }

  async list(includeDeleted = false): Promise<DuaReflection[]> {
    let records = await this.#database.reflections.orderBy("createdAt").reverse().toArray();
    if (!includeDeleted) {
      records = records.filter((record) => record.deletedAt === undefined);
    }
    return records.map((record) => ReflectionSchema.parse(record));
  }

  async listByDua(duaId: string, includeDeleted = false): Promise<DuaReflection[]> {
    let records = await this.#database.reflections.where("duaId").equals(parseEntityId(duaId)).toArray();
    if (!includeDeleted) {
      records = records.filter((record) => record.deletedAt === undefined);
    }
    return records
      .map((record) => ReflectionSchema.parse(record))
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
  }

  async getById(id: string, includeDeleted = false): Promise<DuaReflection | undefined> {
    const record = await this.#database.reflections.get(parseEntityId(id));
    if (record === undefined || (!includeDeleted && record.deletedAt !== undefined)) {
      return undefined;
    }
    return ReflectionSchema.parse(record);
  }

  async create(input: CreateReflectionInput): Promise<DuaReflection> {
    const values = ReflectionCreateSchema.parse(input);
    const timestamp = this.#now();
    return this.#database.transaction(
      "rw",
      this.#database.personalDuas,
      this.#database.people,
      this.#database.reflections,
      async () => {
        const dua = await this.#database.personalDuas.get(values.duaId);
        if (dua === undefined || dua.deletedAt !== undefined) {
          throw new EntityNotFoundError("PersonalDua", values.duaId);
        }
        await assertLiveReferences(this.#database.people, values.personIds, "Person");
        const record = ReflectionSchema.parse({
          id: this.#createId(),
          ...values,
          createdAt: timestamp,
          updatedAt: timestamp,
        });
        await this.#database.reflections.add(record);
        return record;
      },
    );
  }

  async update(id: string, input: UpdateReflectionInput): Promise<DuaReflection> {
    const entityId = parseEntityId(id);
    const patch = ReflectionUpdateSchema.parse(input);
    return this.#database.transaction(
      "rw",
      [this.#database.reflections, this.#database.people],
      async () => {
        const current = await this.#database.reflections.get(entityId);
        if (current === undefined || current.deletedAt !== undefined) {
          throw new EntityNotFoundError("Reflection", entityId);
        }
        if (patch.personIds !== undefined) {
          await assertLiveReferences(this.#database.people, patch.personIds, "Person");
        }
        const updated = ReflectionSchema.parse({ ...current, ...patch, updatedAt: this.#now() });
        await this.#database.reflections.put(updated);
        return updated;
      },
    );
  }

  async softDelete(id: string): Promise<DuaReflection> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.reflections, async () => {
      const current = await this.#database.reflections.get(entityId);
      if (current === undefined) {
        throw new EntityNotFoundError("Reflection", entityId);
      }
      if (current.deletedAt !== undefined) {
        return ReflectionSchema.parse(current);
      }
      const timestamp = this.#now();
      const deleted = ReflectionSchema.parse({ ...current, deletedAt: timestamp, updatedAt: timestamp });
      await this.#database.reflections.put(deleted);
      return deleted;
    });
  }

  async restore(id: string): Promise<DuaReflection> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.reflections, async () => {
      const current = await this.#database.reflections.get(entityId);
      if (current === undefined) {
        throw new EntityNotFoundError("Reflection", entityId);
      }
      const candidate = { ...current };
      delete candidate.deletedAt;
      const restored = ReflectionSchema.parse({ ...candidate, updatedAt: this.#now() });
      await this.#database.reflections.put(restored);
      return restored;
    });
  }
}
